// src/contexts/AuthModalContext.tsx
import React, { createContext, useContext, useState, ReactNode } from 'react'

type AuthModalMode = 'login' | 'signup' | 'forgot-password' | null

interface AuthModalContextType {
  mode: AuthModalMode
  returnTo: string | null
  openLogin: (returnTo?: string) => void
  openSignup: (returnTo?: string) => void
  openForgotPassword: () => void
  close: () => void
  switchToLogin: () => void
  switchToSignup: () => void
}

const AuthModalContext = createContext<AuthModalContextType | undefined>(undefined)

export const AuthModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<AuthModalMode>(null)
  const [returnTo, setReturnTo] = useState<string | null>(null)

  const openLogin = (path?: string) => { setReturnTo(path ?? null); setMode('login') }
  const openSignup = (path?: string) => { setReturnTo(path ?? null); setMode('signup') }
  const openForgotPassword = () => setMode('forgot-password')
  const close = () => { setMode(null); setReturnTo(null) }
  const switchToLogin = () => setMode('login')
  const switchToSignup = () => setMode('signup')

  return (
    <AuthModalContext.Provider value={{ mode, returnTo, openLogin, openSignup, openForgotPassword, close, switchToLogin, switchToSignup }}>
      {children}
    </AuthModalContext.Provider>
  )
}

export const useAuthModal = () => {
  const context = useContext(AuthModalContext)
  if (!context) {
    throw new Error('useAuthModal must be used within AuthModalProvider')
  }
  return context
}
